import {
  useState,
  useCallback,
  useRef,
  useMemo,
  SetStateAction,
  useEffect,
} from 'react';
import { useNavigate } from 'react-router-dom';
import S from './AnnouncementStyles';
import useGetFleetId from '../../../API/hooks/useGetFleetId';
import useGetRole from '../../../API/hooks/useGetRole';
import useGetAudioAnnouncementFiles from '../../../API/hooks/announcement/useGetAudioAnnouncementFiles';
import useUploadAudioAnnouncementFiles from '../../../API/hooks/announcement/useUploadAudioAnnouncementFiles';
import usePatchAudioAnnouncementFileName from '../../../API/hooks/announcement/usePatchAudioAnnouncementFileName';
import useDeleteAudioAnnouncementFile from '../../../API/hooks/announcement/useDeleteAudioAnnouncementFile';
import AudioFileCard from './AudioFileCard/AudioFileCard';
import AddAudioFileCard from './AddAudioFileCard/AddAudioFileCard';
import RecordAudioCard from './RecordAudioFileCard/RecordAudioFileCard';
import AudioRecorder from './RecordAudioFileCard/RecordAudioModalContent';
import BroadcastQueue from './BroadcastQueue/BroadcastQueue';
import UploadAudioFileModal from './UploadAudioFileModal/UploadAudioFileModal';
import Modal from '../../../components/common/Modal/Modal';
import type { ValidatedAudioItem } from './UploadAudioFileModal/UploadAudioFileModal';
import type { AudioAnnouncementUploadResponse } from '../../../API/types/AudioAnnouncement';
import { showUploadResult, toast } from '../../../utils/toast';
import { formatSizeBytes } from '../../../utils/unitsUtils';

const MAX_AUDIO_FILES = 5;
const SIZE_PLACEHOLDER = '—';

export type AudioListItem = {
  id: string;
  name: string;
  durationSec: number | null;
  sizeFormatted: string;
  audioUrl?: string | null;
};

export default function Announcement(): JSX.Element {
  const fleetId = useGetFleetId();
  const { isFleetViewer, isFleetManager } = useGetRole();
  const { data: apiFiles = [], isLoading: listLoading, isError: listError } = useGetAudioAnnouncementFiles(fleetId ?? '');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [recordModalOpen, setRecordModelOpen] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadFailedFileNames, setUploadFailedFileNames] = useState<string[] | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const pendingUploadItemsRef = useRef<ValidatedAudioItem[]>([]);

  const audioList = useMemo((): AudioListItem[] => {
    if (!apiFiles.length) return [];
    return apiFiles.map((f) => ({
      id: f._id,
      name: f.fileName,
      durationSec: f.durationSec ?? null,
      sizeFormatted: f.sizeBytes != null ? formatSizeBytes(f.sizeBytes) : SIZE_PLACEHOLDER,
      audioUrl: f.url,
    }));
  }, [apiFiles]);
  const existingNames = useMemo(
    () => new Set(audioList.map((f) => f.name.toLowerCase())),
    [audioList],
  );

  const handlePlayPause = useCallback((id: string, isPlaying: boolean): void => {
    setPlayingId((prev) => {
      if (isPlaying) return id;
      return prev === id ? null : prev;
    });
  }, []);

  const handleAddClick = useCallback((): void => {
    setUploadError(null);
    setPlayingId(null);

    setUploadModalOpen(true);
  }, []);

  const handleRecordClick = useCallback((): void => {
    setPlayingId(null);
    setRecordModelOpen(true);
  }, []);

  const handleUploadClose = useCallback((): void => {
    setUploadModalOpen(false);
    setUploadError(null);
    setUploadFailedFileNames(null);
  }, []);

  const handleRecordClose = useCallback((): void => {
    setRecordModelOpen(false);
  }, []);

  const handleUploadSuccess = useCallback((response: AudioAnnouncementUploadResponse): void => {
    pendingUploadItemsRef.current = [];
    setUploadError(null);
    const hasFailures = (response.failedFiles?.length ?? 0) > 0;
    if (!hasFailures) {
      setUploadModalOpen(false);
    } else {
      setUploadFailedFileNames(response.failedFiles ?? []);
    }
    showUploadResult(response);
  }, []);

  const handleUploadFailure = useCallback((error: string): void => {
    setUploadError(error);
  }, []);

  const uploadMutation = useUploadAudioAnnouncementFiles(handleUploadSuccess, handleUploadFailure);

  const patchFileNameMutation = usePatchAudioAnnouncementFileName(
    () => toast.success('Filename updated.'),
    (error: string) => toast.error(error),
  );

  const deleteFileMutation = useDeleteAudioAnnouncementFile(
    () => toast.success('Audio file deleted.'),
    (error: string) => toast.error(error),
  );
  const handleUploadFiles = useCallback((items: ValidatedAudioItem[]): void => {
    if (!fleetId) return;
    setUploadError(null);
    pendingUploadItemsRef.current = items;
    uploadMutation.mutate({ fleetId, files: items.map((i) => i.file) });
  }, [fleetId, uploadMutation]);

  const handleEdit = useCallback((id: string, newName: string): void => {
    if (!fleetId) return;
    patchFileNameMutation.mutate({ fleetId, audioId: id, fileName: newName });
  }, [fleetId, patchFileNameMutation]);

  const handleDelete = useCallback((id: string): void => {
    if (!fleetId) return;
    deleteFileMutation.mutate({ fleetId, audioId: id });
  }, [fleetId, deleteFileMutation]);

  const handleToggleUploadModal = useCallback((value: SetStateAction<boolean>): void => {
    setUploadModalOpen(value);
  }, []);

  const navigate = useNavigate();
  const handleSend = useCallback((id: string): void => {
    navigate('/announcements/assign', { state: { audioId: id } });
  }, [navigate]);

  if (isFleetViewer) {
    return (
      <S.Wrapper>
        <S.Error>No permissions to see or change setting!</S.Error>
      </S.Wrapper>
    );
  }

  const canAddMore = audioList.length < MAX_AUDIO_FILES;

  return (
    <S.Wrapper>
      <div className={!(isFleetViewer) ? '' : 'hidden'}>
        <S.Header>
          <S.Title>Announcements</S.Title>
          <S.Actions />
        </S.Header>
        <S.Content>
          <S.BlockContainer>
            <S.RowContainer>
              <S.FiltersBar />
            </S.RowContainer>
            <S.ListArea>
              {listError && <S.Error>Failed to load audio files.</S.Error>}
              {listLoading && !apiFiles.length && <S.Error>Loading...</S.Error>}
              <S.CardList>
                {audioList.map((item) => {
                  const otherNames = new Set(
                    audioList
                      .filter((f) => f.id !== item.id)
                      .map((f) => f.name.toLowerCase()),
                  );
                  return (
                    <AudioFileCard
                      key={item.id}
                      id={item.id}
                      name={item.name}
                      durationSec={item.durationSec}
                      sizeFormatted={item.sizeFormatted}
                      audioUrl={item.audioUrl}
                      playingId={playingId}
                      existingNames={otherNames}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onSend={handleSend}
                      onPlayPause={handlePlayPause}
                    />
                  );
                })}
                {canAddMore && <AddAudioFileCard onClick={handleAddClick} />}
                {canAddMore && <RecordAudioCard onClick={handleRecordClick} />}
              </S.CardList>
            </S.ListArea>
          </S.BlockContainer>
          <BroadcastQueue />
        </S.Content>
      </div>

      {uploadModalOpen && (
        <Modal
          title="Upload Audio File"
          state={uploadModalOpen}
          settings={{ minWidth: '440px' }}
          toggleState={handleToggleUploadModal}
          onClose={handleUploadClose}
        >
          <UploadAudioFileModal
            existingCount={audioList.length}
            existingNames={existingNames}
            onClose={handleUploadClose}
            onUploadFiles={handleUploadFiles}
            isUploading={uploadMutation.isLoading}
            errorMessage={uploadError}
            failedFileNamesFromUpload={uploadFailedFileNames}
            onUploadResultApplied={(): void => setUploadFailedFileNames(null)}
          />
        </Modal>
      )}

      {recordModalOpen && (
        <Modal
          title="Record Announcement"
          state={recordModalOpen}
          settings={{ minWidth: '480px' }}
          toggleState={handleToggleUploadModal}
          onClose={handleRecordClose}
        >
          <AudioRecorder audioList={audioList} fleetId={fleetId} onClose={handleRecordClose} />
        </Modal>
      )}
    </S.Wrapper>
  );
}
